/**
 * Phase 2J-C / Item 2 — Test-record evidence photos UI.
 *
 * Drag-drop / click-to-upload widget with a thumbnail gallery for any
 * passport_test record. Lab staff + admin only. Mounted on
 * /admin/lab/{test_id} via `<EvidencePhotosPanel testId={id} />`.
 *
 * Hard rules mirrored from the backend (`backend/routers/evidence_photos.py`):
 *   • 4 MB per file
 *   • JPG / PNG / WebP / HEIC only
 *   • Max 10 active photos per test
 *   • Photos served via authenticated endpoint only — no public URLs
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Loader2, ImageOff, X } from "lucide-react";
import api from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { Button } from "@/components/ui/button";

const MAX_PHOTOS = 10;
const MAX_BYTES = 4 * 1024 * 1024;
const ALLOWED_TYPES = new Set([
  "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif",
]);

const fmtBytes = (n) => n < 1024 ? `${n} B`
  : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB`
  : `${(n / 1024 / 1024).toFixed(1)} MB`;

const fmtDate = (iso) => formatDateTimeAU(iso);

export default function EvidencePhotosPanel({ testId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [thumbCache, setThumbCache] = useState({});
  const [lightbox, setLightbox] = useState(null);
  const fileInput = useRef(null);

  const refresh = useCallback(async () => {
    if (!testId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tests/${testId}/evidence-photos`);
      setPhotos(data.photos || []);
    } catch (e) {
      toast.error("Failed to load evidence photos");
    } finally { setLoading(false); }
  }, [testId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Pre-load thumbnails as blob URLs (the file endpoint is auth-protected,
  // so we can't use the raw URL in an <img src> — we fetch via axios and
  // store a blob URL per photo id).
  useEffect(() => {
    let cancelled = false;
    const loadOne = async (p) => {
      if (thumbCache[p.id]) return;
      try {
        const { data } = await api.get(
          `/admin/tests/${testId}/evidence-photos/${p.id}/file`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        setThumbCache((prev) => ({ ...prev, [p.id]: url }));
      } catch { /* swallow — thumbnail will fall back to icon */ }
    };
    photos.forEach(loadOne);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, testId]);

  // Clean up blob URLs when component unmounts
  useEffect(() => () => {
    Object.values(thumbCache).forEach((u) => URL.revokeObjectURL(u));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} active photos per test — delete one first`);
      return;
    }
    const available = MAX_PHOTOS - photos.length;
    const toUpload = Array.from(files).slice(0, available);
    if (files.length > available) {
      toast.warning(`Only uploading ${available} file(s) to stay within the ${MAX_PHOTOS}-photo limit`);
    }

    setUploading(true);
    try {
      for (const file of toUpload) {
        if (!ALLOWED_TYPES.has(file.type)) {
          toast.error(`${file.name}: unsupported type (${file.type})`);
          continue;
        }
        if (file.size > MAX_BYTES) {
          toast.error(`${file.name}: exceeds 4 MB limit`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        if (caption) fd.append("caption", caption);
        try {
          await api.post(`/admin/tests/${testId}/evidence-photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`Uploaded ${file.name}`);
        } catch (e) {
          toast.error(`${file.name}: ${e.response?.data?.detail || "upload failed"}`);
        }
      }
      setCaption("");
      await refresh();
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (photoId, filename) => {
    if (!window.confirm(`Delete ${filename || "this photo"}? (soft-delete — audit trail retained)`)) {
      return;
    }
    try {
      await api.delete(`/admin/tests/${testId}/evidence-photos/${photoId}`);
      toast.success("Photo deleted");
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      const url = thumbCache[photoId];
      if (url) URL.revokeObjectURL(url);
      setThumbCache(({ [photoId]: _, ...rest }) => rest);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section data-testid="evidence-photos-panel" className="space-y-3">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Evidence photos</h3>
          <p className="text-xs text-slate-500 max-w-xl mt-0.5">
            Attach photos of sample arrival, bottle condition, COA scans, or site photos.
            Internal use only — not visible to customers and not attached to reports.
            {" "}{photos.length}/{MAX_PHOTOS} active.
          </p>
        </div>
        <Button
          data-testid="evidence-photos-upload-btn"
          size="sm"
          variant="outline"
          disabled={uploading || photos.length >= MAX_PHOTOS}
          onClick={() => fileInput.current?.click()}
          className="rounded-full text-xs"
        >
          {uploading
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading…</>
            : <><Upload className="w-3.5 h-3.5 mr-1.5" />Add photo</>}
        </Button>
      </div>

      {/* Drag-drop zone (also the click target via the hidden input) */}
      <div
        data-testid="evidence-photos-dropzone"
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInput.current?.click()}
        className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition ${
          dragOver
            ? "border-cyan-400 bg-cyan-50/60"
            : "border-slate-200 bg-slate-50/50 hover:border-slate-300"
        }`}
      >
        <Upload className="w-5 h-5 mx-auto text-slate-400 mb-1.5" />
        <div className="text-xs text-slate-600 font-medium">
          Drag &amp; drop photos here, or click to browse
        </div>
        <div className="text-[10px] text-slate-400 mt-1">
          Up to 10 photos · 4 MB each · JPG, PNG, WebP, HEIC
        </div>
        <input
          ref={fileInput}
          data-testid="evidence-photos-file-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
          Caption / note (optional, applied to the next upload)
        </label>
        <input
          data-testid="evidence-photos-caption"
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={500}
          placeholder="e.g. Sample arrived with cracked seal"
          className="w-full rounded-xl border-slate-200 text-xs px-3 py-1.5"
        />
      </div>

      {/* Gallery */}
      <div>
        {loading && photos.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-xs">Loading…</div>
        ) : photos.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-xs">No photos attached yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`evidence-photo-${p.id}`}
                className="relative bg-white rounded-xl border border-slate-200 overflow-hidden group"
              >
                <button
                  type="button"
                  onClick={() => setLightbox(p)}
                  className="aspect-square w-full bg-slate-100 flex items-center justify-center overflow-hidden"
                >
                  {thumbCache[p.id]
                    ? <img src={thumbCache[p.id]} alt={p.caption || p.original_filename}
                           className="w-full h-full object-cover" />
                    : <ImageOff className="w-6 h-6 text-slate-300" />}
                </button>
                <div className="px-2 py-1.5">
                  <div className="text-[10px] text-slate-700 font-semibold truncate"
                       title={p.original_filename || ""}>
                    {p.original_filename || "photo"}
                  </div>
                  <div className="text-[9px] text-slate-400 truncate">
                    {fmtDate(p.uploaded_at)} · {p.uploaded_by_role}
                  </div>
                  {p.caption && (
                    <div className="text-[10px] text-slate-600 italic truncate mt-0.5"
                         title={p.caption}>{p.caption}</div>
                  )}
                  <div className="text-[9px] text-slate-400">{fmtBytes(p.size_bytes)}</div>
                </div>
                <button
                  data-testid={`evidence-photo-delete-${p.id}`}
                  onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.original_filename); }}
                  className="absolute top-1.5 right-1.5 p-1 rounded-full bg-white/90 text-rose-500 opacity-0 group-hover:opacity-100 transition shadow"
                  title="Delete (soft)"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox modal */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {thumbCache[lightbox.id] && (
              <img src={thumbCache[lightbox.id]}
                   alt={lightbox.caption || lightbox.original_filename}
                   className="max-w-full max-h-[80vh] object-contain mx-auto rounded" />
            )}
            <div className="text-white text-center mt-3 text-sm">
              <div className="font-semibold">{lightbox.original_filename}</div>
              {lightbox.caption && <div className="italic text-slate-300">{lightbox.caption}</div>}
              <div className="text-xs text-slate-400 mt-1">
                {fmtDate(lightbox.uploaded_at)} · uploaded by {lightbox.uploaded_by_email} ({lightbox.uploaded_by_role}) · {fmtBytes(lightbox.size_bytes)}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
